import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { NavStatus } from './types/navigation-types';

interface NavalEncounterGeneratorProps {
  weatherData: {
    climate: string;
    temperature: string;
    wind: string;
    windStrength: string;
    windDirection: string;
    precipitation: string;
  };
  voyageDay: number;
  crewSize: number;
  provisions: number;
  navStatus: NavStatus;
  destinationName?: string;
}

const API_ENDPOINT = 'https://kbydfuxse7.execute-api.us-east-1.amazonaws.com/prod/generate-encounter';

/**
 * The model emits its section labels as a standalone bold line ("**DM Notes**")
 * rather than a heading. CommonMark treats the following line as part of the
 * same paragraph, so the label runs straight into the prose. Promote any line
 * that is nothing but bold text to a real heading so it gets heading spacing.
 */
const normalizeEncounterMarkdown = (text: string): string =>
  text.replace(/^[ \t]*\*\*(.+?)\*\*[ \t]*:?[ \t]*$/gm, '### $1');

/** Tailwind styling per element — the typography plugin isn't installed. */
const MARKDOWN_COMPONENTS = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-xl font-bold mt-6 mb-3 first:mt-0">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-lg font-bold mt-6 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-base font-semibold mt-5 mb-2 first:mt-0 text-blue-700 dark:text-blue-300">
      {children}
    </h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-3 leading-relaxed">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc pl-5 mb-3 space-y-1.5">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal pl-5 mb-3 space-y-1.5">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-gray-900 dark:text-gray-50">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="italic">{children}</em>
  ),
  hr: () => <hr className="my-5 border-gray-300 dark:border-gray-600" />,
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-3">
      {children}
    </blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="text-left p-2 border-b font-semibold">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="p-2 border-b align-top">{children}</td>
  )
};

const NAV_STATUS_TEXT: Record<NavStatus, string> = {
  'not-charted': 'No course has been set yet.',
  'on-course': 'The ship is on course and the crew knows where they are.',
  'lost-unaware': 'DM ONLY: the ship has drifted off course, but the crew still believes they are on the plotted line.',
  'lost-aware': 'The crew knows they are lost and is trying to re-establish their position.',
  'arrived': 'The ship has reached its destination.'
};

const NavalEncounterGenerator: React.FC<NavalEncounterGeneratorProps> = ({
  weatherData,
  voyageDay,
  crewSize,
  provisions,
  navStatus,
  destinationName
}) => {
  const [additionalContext, setAdditionalContext] = useState('');
  const [encounter, setEncounter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateEncounter = async () => {
    setIsLoading(true);
    setError(null);

    const daysOfFood = crewSize > 0 ? Math.floor(provisions / crewSize) : null;
    const suppliesLine = daysOfFood === null
      ? `Provisions: ${provisions} points, no crew aboard.`
      : `Provisions: ${provisions} points for ${crewSize} crew — ${daysOfFood} days of food left.`;

    const prompt = `You are a creative D&D Dungeon Master generating a naval encounter. Create an engaging, mechanically-sound encounter that gives the DM actionable narrative and gameplay elements.

CONTEXT:
Weather: ${weatherData.climate}, ${weatherData.temperature}
Wind: ${weatherData.wind} (${weatherData.windStrength}) from ${weatherData.windDirection}
Precipitation: ${weatherData.precipitation}

Voyage State:
Day ${voyageDay} of the voyage${destinationName ? `, bound for ${destinationName}` : ''}.
${suppliesLine}
Navigation: ${NAV_STATUS_TEXT[navStatus]}

${additionalContext ? `Campaign Context: ${additionalContext}\n` : ''}
GUIDANCE:
- Weather conditions should create narrative atmosphere and suggest mechanical consequences (visibility issues, difficult ship handling, environmental challenges)
- Let the voyage state set the pressure: dwindling provisions make every delay costly, and a crew that is lost (especially one that doesn't yet know it) should meet things that hint at how far off course they are
- Provide multiple ways players might engage with this encounter (combat, negotiation, stealth, creativity, avoidance)

PROVIDE THE FOLLOWING:

**Encounter Hook** (What the players see, hear, smell, or feel - 2-3 vivid sentences)

**The Situation** (What's actually happening and why - include NPC motivations if applicable)

**Weather & Environmental Impact**
How do the current conditions affect this encounter? Consider:
- Visibility and perception
- Ship maneuverability and speed
- Character actions and movement
- Atmospheric tension

**Possible Approaches**
Describe 3-4 different ways players might engage:
- Direct confrontation
- Diplomatic/social solution
- Clever/creative approach
- Evasion or retreat
What are the natural consequences of each?

**Voyage Impact**
Based on how players resolve this encounter:
- Does this cost them days, provisions, or crew? How many?
- Does it help or hurt their ability to know where they are?
- What changes about the voyage from here on?

**Complications & Opportunities**
- What could go wrong (beyond simple failure)?
- What unexpected benefits might players discover?
- How might this encounter ripple into future sessions?

**DM Notes**
- Key details to track or remember
- Suggested enemy/NPC types (name creatures, but don't provide stats)
- Potential treasure, discoveries, or narrative rewards
- Pacing notes (quick skirmish vs extended scene)

Keep the total response under 450 words but prioritize actionable, evocative detail over generic description.`;

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `API error: ${response.status} ${response.statusText}`
        );
      }
  
      const data = await response.json();
      if (data.generatedContent) {
        setEncounter(data.generatedContent);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setEncounter('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // max-w-5xl (1024px) is about as wide as this prose reads comfortably.
    // Bump to 6xl/7xl or drop the cap entirely for full container width.
    <Card className="w-full max-w-5xl mx-auto mt-4">
      <CardHeader>
        <CardTitle>Naval Encounter Generator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Textarea
            placeholder="Additional context (optional)"
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
          />

          <Button
            onClick={generateEncounter}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Encounter'
            )}
          </Button>

          {error && (
            <div className="mt-4 p-4 bg-red-100 dark:bg-red-900 rounded-md text-red-900 dark:text-red-100">
              <h4 className="font-semibold mb-2">Error:</h4>
              <p>{error}</p>
            </div>
          )}

          {encounter && (
            <div className="mt-4 p-6 bg-gray-100 dark:bg-gray-800 rounded-md text-gray-900 dark:text-gray-100">
              <h4 className="font-semibold mb-4 pb-2 border-b border-gray-300 dark:border-gray-600">
                Generated Encounter
              </h4>
              <div className="text-sm">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  components={MARKDOWN_COMPONENTS}
                >
                  {normalizeEncounterMarkdown(encounter)}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NavalEncounterGenerator;